import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { BehaviorSubject, catchError, filter, map, of, switchMap, tap } from 'rxjs';

import { TimeSheetTask } from '../models/time-sheet-task';
import { TimesheetSavePayload } from '../models/timesheet-save-payload';

@Injectable({
  providedIn: 'root',
})
export class TimeSheetService {
  private http = inject(HttpClient);

  private readonly refresh$ = new BehaviorSubject<{ year: number; month: number } | null>(null);

  refreshTimeSheetData(year: number, month: number) {
    this.refresh$.next({ year, month });
  }

  private readonly _timeSheetData$ = this.refresh$.pipe(
    filter((r) => r !== null),
    switchMap((params) =>
      this.http.get<TimeSheetTask[]>(`/timesheet/${params?.year}/${params?.month}`).pipe(
        map((data) =>
          data.map((t) => ({
            ...t,
            start: t.start ? new Date(t.start) : undefined,
            end: t.end ? new Date(t.end) : undefined,
          }))
        ),
        catchError(() => of([]))
      )
    )
  );

  readonly timeSheetData$ = this._timeSheetData$;

  savetimeSheetDate(timeSheetDate: TimesheetSavePayload) {
    return this.http
      .put(`/timesheet/${timeSheetDate.year}/${timeSheetDate.month}/save`, timeSheetDate.entries)
      .pipe(tap(() => this.refreshTimeSheetData(timeSheetDate.year, timeSheetDate.month)));
  }

  completeTimeSheet(year: number, month: number) {
    return this.http
      .patch(`/timesheet/${year}/${month}/complete`, {})
      .pipe(tap(() => this.refreshTimeSheetData(year, month)));
  }

  generateTimeSheet(year: number, month: number, options?: { primaryProject?: string | null }) {
    const params: Record<string, unknown> = {};
    if (options?.primaryProject) {
      params['primaryProject'] = options.primaryProject;
    }

    return this.http
      .post(`/timesheet/${year}/${month}/generate`, params)
      .pipe(tap(() => this.refreshTimeSheetData(year, month)));
  }
}
